import { createClient } from '@libsql/client';

const client = createClient({
  url: import.meta.env.VITE_TURSO_DB_URL,
  authToken: import.meta.env.VITE_TURSO_AUTH_TOKEN
});

const parseProperty = (row) => ({
  id: row.id,
  title: row.title,
  slug: row.slug,
  description: row.description,
  price: row.price,
  location: {
    address: row.address,
    city: row.city,
    state: row.state,
    coordinates: {
      lat: row.lat,
      lng: row.lng,
    },
  },
  features: {
    bedrooms: row.bedrooms,
    bathrooms: row.bathrooms,
    area: row.area,
    parking: row.parking,
    yearBuilt: row.year_built,
  },
  amenities: JSON.parse(row.amenities || '[]'),
  images: JSON.parse(row.images || '[]'),
  propertyType: row.property_type,
  status: row.status,
  featured: Boolean(row.featured),
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  views: row.views || 0, 
});

const createSlug = (title, id) => {
  return `${title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')}-${id}`;
};

export const tursoAPI = {
  createProperty: async (property) => {
    try {
      const id = crypto.randomUUID();
      const slug = createSlug(property.title, id);
      const createdAt = new Date().toISOString();

      const result = await client.execute(`
        INSERT INTO properties (
          id, title, slug, description, price, address, city, state, lat, lng,
          bedrooms, bathrooms, area, parking, year_built, amenities, images,
          property_type, status, featured, created_at, updated_at, views
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        RETURNING *
      `, [
        id,
        property.title,
        slug,
        property.description,
        property.price,
        property.location.address,
        property.location.city,
        property.location.state,
        property.location.coordinates.lat,
        property.location.coordinates.lng,
        property.features.bedrooms,
        property.features.bathrooms,
        property.features.area,
        property.features.parking,
        property.features.yearBuilt,
        JSON.stringify(property.amenities),
        JSON.stringify(property.images),
        property.propertyType,
        property.status,
        property.featured ? 1 : 0,
        createdAt,
        createdAt,
        0,
      ]);

      return parseProperty(result.rows[0]);
    } catch (error) {
      console.error('Error creating property:', error);
      throw error;
    }
  },

  getAllProperties: async (filters = {}) => {
    try {
      let sql = 'SELECT * FROM properties WHERE 1=1';
      const args = [];

      if (filters.property_type) {
        sql += ' AND property_type = ?';
        args.push(filters.property_type);
      }
      if (filters.status) {
        sql += ' AND status = ?';
        args.push(filters.status);
      }
      if (filters.bedrooms) {
        const bedrooms = parseInt(filters.bedrooms);
        if (filters.bedrooms === '5+') {
          sql += ' AND bedrooms >= 5';
        } else {
          sql += ' AND bedrooms = ?';
          args.push(bedrooms);
        }
      }
      if (filters.bathrooms) {
        const bathrooms = parseInt(filters.bathrooms);
        if (filters.bathrooms === '4+') {
          sql += ' AND bathrooms >= 4';
        } else {
          sql += ' AND bathrooms = ?';
          args.push(bathrooms);
        }
      }
      if (filters.location) {
        sql += ' AND (address LIKE ? OR city LIKE ? OR state LIKE ?)';
        args.push(`%${filters.location}%`, `%${filters.location}%`, `%${filters.location}%`);
      }
      if (filters.minPrice) {
        sql += ' AND price >= ?';
        args.push(parseFloat(filters.minPrice));
      }
      if (filters.maxPrice) {
        sql += ' AND price <= ?';
        args.push(parseFloat(filters.maxPrice));
      }
      if (filters.min_area) {
        sql += ' AND area >= ?';
        args.push(parseFloat(filters.min_area));
      }
      if (filters.max_area) {
        sql += ' AND area <= ?';
        args.push(parseFloat(filters.max_area));
      }
      if (filters.parking) {
        const parking = parseInt(filters.parking);
        if (filters.parking === '3+') {
          sql += ' AND parking >= 3';
        } else {
          sql += ' AND parking = ?';
          args.push(parking);
        }
      }
      if (filters.amenities && filters.amenities.length > 0) {
        filters.amenities.forEach(amenity => {
          sql += ' AND amenities LIKE ?';
          args.push(`%${amenity}%`);
        });
      }

      sql += ' ORDER BY created_at DESC';

      const result = await client.execute({ sql, args });
      return { properties: result.rows.map(parseProperty) };
    } catch (error) {
      console.error('Error fetching all properties:', error);
      throw error;
    }
  },

  getPropertyById: async (id) => {
    try {
      const result = await client.execute({
        sql: 'SELECT * FROM properties WHERE id = ?',
        args: [id],
      });

      if (!result.rows || result.rows.length === 0) {
        return null;
      }

      return parseProperty(result.rows[0]);
    } catch (error) {
      console.error('Error fetching property by ID:', error);
      throw error;
    }
  },

  getPropertyBySlug: async (slug) => {
    try {
      const cleanSlug = slug?.trim();
      console.log('Slug from caller:', cleanSlug);

      const result = await client.execute({
        sql: 'SELECT * FROM properties WHERE slug = ?',
        args: [cleanSlug],
      });

      console.log('DB result rows:', result.rows);

      if (!result.rows || result.rows.length === 0) {
        return null;
      }

      return parseProperty(result.rows[0]);
    } catch (error) {
      console.error('Error fetching property by slug:', error);
      throw error;
    }
  },

  incrementViews: async (propertyId) => {
    try {
      const updatePropertyViewsResult = await client.execute({
        sql: 'UPDATE properties SET views = views + 1 WHERE id = ? RETURNING views',
        args: [propertyId],
      });

      const today = new Date().toISOString().split('T')[0]; 
      await client.execute({
        sql: `
          INSERT INTO daily_views (view_date, property_id, views_count)
          VALUES (?, ?, 1)
          ON CONFLICT(view_date, property_id) DO UPDATE SET views_count = views_count + 1;
        `,
        args: [today, propertyId],
      });

      return updatePropertyViewsResult.rows[0].views;
    } catch (error) {
      console.error('Error incrementing views and daily views:', error);
      throw error;
    }
  },

  getTotalViews: async () => {
    try {
      const result = await client.execute('SELECT SUM(views) as totalViews FROM properties');
      return result.rows[0].totalViews || 0;
    } catch (error) {
      console.error('Error fetching total views:', error);
      throw error;
    }
  },

  getTotalDailyViews: async (startDate, endDate) => {
    try {
      const result = await client.execute({
        sql: `
          SELECT view_date, SUM(views_count) as daily_views
          FROM daily_views
          WHERE view_date BETWEEN ? AND ?
          GROUP BY view_date
          ORDER BY view_date ASC
        `,
        args: [startDate, endDate],
      });
      return result.rows.map(row => ({
        date: row.view_date,
        Views: row.daily_views || 0,
      }));
    } catch (error) {
      console.error('Error fetching total daily views:', error);
      throw error;
    }
  },

  updateProperty: async (id, updatedProperty) => {
    try {
      const { title, description, price, location, features, amenities, images, propertyType, status, featured } = updatedProperty;

      const result = await client.execute(`
        UPDATE properties
        SET
          title = ?,
          description = ?,
          price = ?,
          address = ?,
          city = ?,
          state = ?,
          lat = ?,
          lng = ?,
          bedrooms = ?,
          bathrooms = ?,
          area = ?,
          parking = ?,
          year_built = ?,
          amenities = ?,
          images = ?,
          property_type = ?,
          status = ?,
          featured = ?,
          updated_at = CURRENT_TIMESTAMP
        WHERE id = ?
        RETURNING *
      `, [
        title,
        description,
        price,
        location.address,
        location.city,
        location.state,
        location.coordinates.lat,
        location.coordinates.lng,
        features.bedrooms,
        features.bathrooms,
        features.area,
        features.parking,
        features.yearBuilt,
        JSON.stringify(amenities),
        JSON.stringify(images),
        propertyType,
        status,
        featured ? 1 : 0,
        id,
      ]);

      if (result.rows.length === 0) {
        throw new Error('Property not found');
      }

      return parseProperty(result.rows[0]);
    } catch (error) {
      console.error('Error updating property:', error);
      throw error;
    }
  },

  deleteProperty: async (id) => {
    try {
      await client.execute('DELETE FROM properties WHERE id = ?', [id]);
      return true;
    } catch (error) {
      console.error('Error deleting property:', error);
      throw error;
    }
  },

  getFeaturedProperties: async (limit = 3) => {
    try {
      const result = await client.execute({
        sql: 'SELECT * FROM properties WHERE featured = 1 ORDER BY created_at DESC LIMIT ?',
        args: [limit],
      });
      return { properties: result.rows.map(parseProperty) };
    } catch (error) {
      console.error('Error fetching featured properties:', error);
      throw error;
    }
  }
};